import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Rocket, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { adminService } from "../services/adminService";

function Stat({ label, value, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    success: "text-success",
    warning: "text-accent",
    danger: "text-destructive",
  };
  return (
    <div className="rounded-2xl border border-border bg-cream/40 p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display text-3xl mt-1 ${tones[tone] || tones.default}`}>{value}</div>
    </div>
  );
}

export default function SuperAdminFirmwarePage() {
  const [status, setStatus] = useState(null);
  const [version, setVersion] = useState("");
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const refreshFirmwareStatus = useCallback(async () => {
    const res = await adminService.getFirmwareStatus();
    if (res.ok) {
      const item = res.data?.item || null;
      setStatus(item);
      setVersion((current) => current || item?.source?.version || "");
      setNotes((current) => current || item?.source?.changelog || "");
    }
  }, []);

  useEffect(() => {
    refreshFirmwareStatus();
  }, [refreshFirmwareStatus]);

  const canSendUpdate = useMemo(() => {
    if (!version.trim()) return false;
    if (!file && !url.trim()) return false;
    if (!status) return true;
    return status.sourceHasUpdate || version.trim() !== (status.publishedVersion || "");
  }, [file, status, url, version]);

  async function handlePublish() {
    if (!version.trim()) {
      toast.warning("Enter a firmware version (e.g. 2.2.0)");
      return;
    }

    setPublishing(true);
    try {
      let firmwareUrl = url.trim();
      if (file) {
        const up = await adminService.uploadFirmware(file);
        if (!up.ok) throw new Error(up.error || "Firmware upload failed");
        firmwareUrl = up.data?.url || "";
      }
      if (!firmwareUrl) {
        toast.warning("Upload a .bin file or paste a firmware URL");
        return;
      }

      const res = await adminService.publishGlobalFirmware({
        version: version.trim(),
        url: firmwareUrl,
        notes: notes.trim(),
      });
      if (!res.ok) throw new Error(res.error || "Publish failed");

      toast.success(`Update v${version.trim()} sent — users notified to accept`);
      setFile(null);
      await refreshFirmwareStatus();
    } catch (error) {
      toast.error(error?.message || "Publish failed");
    } finally {
      setPublishing(false);
    }
  }

  const rollout = status?.rollout?.summary || null;
  const rolloutItems = status?.rollout?.items || [];

  return (
    <PageShell>
      <PageHero
        eyebrow="Super Admin"
        title="Device firmware"
        highlight="rollout."
        description="Publish OTA updates. Users get a notification and must accept before devices install."
      />
      <PageSection variant="content">
        <div className="mx-auto max-w-6xl grid gap-6">
          <div className="flex flex-wrap gap-2">
            <Link to="/super-admin" className="px-4 py-2 rounded-full border border-border text-sm">Back to Super Admin</Link>
            <Button variant="outline" size="sm" onClick={refreshFirmwareStatus}><RefreshCw className="h-4 w-4" /> Refresh</Button>
          </div>

          {status?.sourceHasUpdate ? (
            <Card className="border-accent/40 bg-accent/5">
              <CardContent className="py-4 text-sm">
                <b>Source code updated.</b> Repo firmware is v{status?.source?.version} but published release is v{status?.publishedVersion || "none"}.
                Build a new .bin, upload it, then click <b>Send update</b>.
                {status?.source?.changelog ? <div className="mt-2 text-muted-foreground">{status.source.changelog}</div> : null}
              </CardContent>
            </Card>
          ) : null}

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Source version" value={status?.source?.version || "—"} />
            <Stat label="Published" value={status?.publishedVersion || "—"} />
            <Stat label="Claimed devices" value={String(status?.claimedDevices ?? 0)} />
            <Stat label="Online now" value={String(rollout?.online ?? 0)} tone="success" />
          </div>

          {rollout ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Stat label="Pending user" value={String(rollout.pending)} tone="warning" />
              <Stat label="Accepted" value={String(rollout.accepted)} />
              <Stat label="Applied" value={String(rollout.applied)} tone="success" />
              <Stat label="Rejected" value={String(rollout.rejected)} tone="danger" />
              <Stat label="Failed" value={String(rollout.failed)} tone="danger" />
            </div>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-accent" />
                Send update to fleet
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 max-w-2xl">
              <p className="text-sm text-muted-foreground">
                Export binary from <code className="text-xs">firmware/esp8266-smart-farm/</code> in Arduino IDE. Users receive a notification and must accept before devices OTA.
              </p>
              <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Version (match manifest.json)" value={version} onChange={(e) => setVersion(e.target.value)} />
              <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Firmware URL (optional if uploading file)" value={url} onChange={(e) => setUrl(e.target.value)} />
              <label className="flex items-center gap-2 rounded-xl border border-dashed border-border px-4 py-6 cursor-pointer hover:bg-secondary/30">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{file ? file.name : "Upload compiled .bin"}</span>
                <input type="file" accept=".bin" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <textarea className="min-h-20 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Release notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Button disabled={publishing || !canSendUpdate} onClick={handlePublish}>
                {publishing ? "Sending..." : canSendUpdate ? "Send update & notify users" : "Build .bin and set version to enable"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Device rollout status</CardTitle></CardHeader>
            <CardContent className="grid gap-2 text-sm">
              {!rolloutItems.length ? <div className="text-muted-foreground">No active rollout yet.</div> : null}
              {rolloutItems.map((d) => (
                <div key={d.deviceId} className="rounded-xl border border-border p-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">{d.deviceId}</div>
                    <div className="text-xs text-muted-foreground">Owner: {d.ownerUid || "—"} · Target v{d.targetVersion}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={d.online ? "text-success" : "text-muted-foreground"}>{d.online ? "Online" : "Offline"}</span>
                    <span className="rounded-full border border-border px-2 py-1 uppercase tracking-wider">{d.status.replace("_", " ")}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </PageSection>
    </PageShell>
  );
}
